<script>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import AppointmentModal from 'dashboard/routes/dashboard/appointments/components/AppointmentModal.vue';

export default {
  name: 'AppointmentsTab',
  components: {
    NextButton,
    Spinner,
    AppointmentModal,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
    contact: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const store = useStore();
    const appointments = ref([]);
    const isLoading = ref(false);
    const showModal = ref(false);
    const selectedAppointment = ref(null);
    const selectedDate = ref(null);

    const contactName = computed(() => props.contact?.name || 'Cliente');
    const contactId = computed(() => props.contact?.id);
    const accountId = computed(() => store.getters.getCurrentAccountId);

    const fetchAppointments = async () => {
      if (!contactId.value) return;

      isLoading.value = true;
      try {
        await store.dispatch('appointments/fetchByContact', contactId.value);
        appointments.value = store.getters['appointments/getByContact'](contactId.value) || [];
      } catch (error) {
        console.error('Error fetching appointments:', error);
        useAlert(t('APPOINTMENTS.API.ERROR_LOADING'));
      } finally {
        isLoading.value = false;
      }
    };

    const createAppointment = () => {
      selectedAppointment.value = null;
      selectedDate.value = new Date();
      showModal.value = true;
    };

    const editAppointment = async (appointment) => {
      try {
        const fullAppointment = await store.dispatch('appointments/fetchAppointment', appointment.id);
        selectedAppointment.value = fullAppointment;
        selectedDate.value = null;
        showModal.value = true;
      } catch (error) {
        console.error('Error fetching appointment:', error);
        selectedAppointment.value = appointment;
        selectedDate.value = null;
        showModal.value = true;
      }
    };

    const closeModal = () => {
      showModal.value = false;
      selectedAppointment.value = null;
      selectedDate.value = null;
    };

    const handleSaveAppointment = async (appointmentData) => {
      try {
        if (selectedAppointment.value) {
          await store.dispatch('appointments/updateAppointment', {
            id: selectedAppointment.value.id,
            ...appointmentData,
          });
          useAlert(t('APPOINTMENTS.MESSAGES.UPDATED'));
        } else {
          await store.dispatch('appointments/createAppointment', appointmentData);
          useAlert(t('APPOINTMENTS.MESSAGES.CREATED'));
        }
        closeModal();
        fetchAppointments();
      } catch (error) {
        console.error('Error saving appointment:', error);
        useAlert(t('APPOINTMENTS.MESSAGES.ERROR'));
      }
    };

    const deleteAppointment = async (appointment) => {
      if (!confirm(t('APPOINTMENTS.DELETE.CONFIRM'))) return;
      try {
        await store.dispatch('appointments/deleteAppointment', appointment.id);
        fetchAppointments();
        useAlert(t('APPOINTMENTS.API.DELETE_SUCCESS'));
      } catch (error) {
        useAlert(t('APPOINTMENTS.API.ERROR_DELETING'));
      }
    };

    const handleDeleteFromModal = async (id) => {
      try {
        await store.dispatch('appointments/deleteAppointment', id);
        closeModal();
        fetchAppointments();
        useAlert(t('APPOINTMENTS.API.DELETE_SUCCESS'));
      } catch (error) {
        useAlert(t('APPOINTMENTS.API.ERROR_DELETING'));
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    };

    const formatTime = (dateString) => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const getStatusClass = (status) => {
      const classes = {
        scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      };
      return classes[status] || classes.scheduled;
    };

    const getStatusLabel = (status) => {
      const labels = {
        scheduled: t('APPOINTMENTS.STATUS.SCHEDULED'),
        completed: t('APPOINTMENTS.STATUS.COMPLETED'),
        cancelled: t('APPOINTMENTS.STATUS.CANCELLED'),
      };
      return labels[status] || status;
    };

    const isUpcoming = (appointment) => {
      return new Date(appointment.start_time) >= new Date();
    };

    const isPast = (appointment) => {
      return new Date(appointment.end_time) < new Date();
    };

    const upcomingAppointments = computed(() => {
      return appointments.value
        .filter(a => isUpcoming(a) && a.status !== 'cancelled')
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    });

    const pastAppointments = computed(() => {
      return appointments.value
        .filter(a => isPast(a) || a.status === 'cancelled')
        .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
    });

    const summary = computed(() => {
      const total = appointments.value.length;
      const scheduled = appointments.value.filter(a => a.status === 'scheduled' && isUpcoming(a)).length;
      const completed = appointments.value.filter(a => a.status === 'completed').length;
      const cancelled = appointments.value.filter(a => a.status === 'cancelled').length;
      return { total, scheduled, completed, cancelled };
    });

    // Pre-fill data for new appointment
    const getPrefilledData = () => {
      return {
        contact_id: contactId.value,
        conversation_id: Number(props.conversationId),
      };
    };

    watch(() => props.contact?.id, (newId) => {
      if (newId) {
        fetchAppointments();
      }
    });

    watch(() => props.conversationId, (newId) => {
      if (newId && contactId.value) {
        fetchAppointments();
      }
    });

    onMounted(() => {
      fetchAppointments();
    });

    return {
      appointments,
      isLoading,
      showModal,
      selectedAppointment,
      selectedDate,
      contactName,
      contactId,
      accountId,
      upcomingAppointments,
      pastAppointments,
      summary,
      createAppointment,
      editAppointment,
      closeModal,
      handleSaveAppointment,
      handleDeleteFromModal,
      deleteAppointment,
      formatDate,
      formatTime,
      formatDateTime,
      getStatusClass,
      getStatusLabel,
      getPrefilledData,
      t,
    };
  },
};
</script>

<template>
  <div class="appointments-tab h-full flex flex-col bg-white dark:bg-slate-900">
    <!-- Header -->
    <div class="p-4 border-b border-slate-200 dark:border-slate-700">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h2 class="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {{ t('APPOINTMENTS.TITLE') }}
          </h2>
          <p class="text-sm text-slate-500">
            {{ contactName }}
          </p>
        </div>
        <NextButton
          :label="t('APPOINTMENTS.NEW')"
          icon="i-lucide-plus"
          @click="createAppointment"
        />
      </div>

      <!-- Summary Cards -->
      <div class="grid grid-cols-4 gap-3">
        <div class="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 text-center">
          <div class="text-2xl font-bold text-slate-800 dark:text-slate-100">
            {{ summary.total }}
          </div>
          <div class="text-xs text-slate-500">{{ t('APPOINTMENTS.SUMMARY.TOTAL') }}</div>
        </div>
        <div class="bg-blue-50 dark:bg-blue-900 rounded-lg p-3 text-center">
          <div class="text-2xl font-bold text-blue-600 dark:text-blue-300">
            {{ summary.scheduled }}
          </div>
          <div class="text-xs text-blue-500">{{ t('APPOINTMENTS.SUMMARY.SCHEDULED') }}</div>
        </div>
        <div class="bg-green-50 dark:bg-green-900 rounded-lg p-3 text-center">
          <div class="text-2xl font-bold text-green-600 dark:text-green-300">
            {{ summary.completed }}
          </div>
          <div class="text-xs text-green-500">{{ t('APPOINTMENTS.SUMMARY.COMPLETED') }}</div>
        </div>
        <div class="bg-red-50 dark:bg-red-900 rounded-lg p-3 text-center">
          <div class="text-2xl font-bold text-red-600 dark:text-red-300">
            {{ summary.cancelled }}
          </div>
          <div class="text-xs text-red-500">{{ t('APPOINTMENTS.SUMMARY.CANCELLED') }}</div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-auto p-4">
      <!-- Loading -->
      <div v-if="isLoading" class="flex justify-center items-center h-full">
        <Spinner />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="appointments.length === 0"
        class="flex flex-col items-center justify-center h-full text-center"
      >
        <div class="i-lucide-calendar-x text-6xl text-slate-300 dark:text-slate-600 mb-4" />
        <h3 class="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
          {{ t('APPOINTMENTS.EMPTY.TITLE') }}
        </h3>
        <p class="text-sm text-slate-500 mb-4">
          {{ t('APPOINTMENTS.EMPTY.DESCRIPTION') }}
        </p>
        <NextButton
          :label="t('APPOINTMENTS.NEW')"
          icon="i-lucide-plus"
          @click="createAppointment"
        />
      </div>

      <!-- Appointments List -->
      <div v-else class="space-y-6">
        <!-- Upcoming Appointments -->
        <div v-if="upcomingAppointments.length > 0">
          <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
            <span class="i-lucide-calendar-clock text-blue-500" />
            {{ t('APPOINTMENTS.UPCOMING') }}
          </h3>
          <div class="space-y-2">
            <div
              v-for="appointment in upcomingAppointments"
              :key="appointment.id"
              class="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow cursor-pointer"
              @click="editAppointment(appointment)"
            >
              <div class="flex items-start justify-between">
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <div
                      class="w-3 h-3 rounded-full flex-shrink-0"
                      :style="{ backgroundColor: appointment.color || '#3b82f6' }"
                    />
                    <h4 class="font-medium text-slate-800 dark:text-slate-100 truncate">
                      {{ appointment.title }}
                    </h4>
                  </div>
                  <div class="flex items-center gap-4 text-sm text-slate-500">
                    <span class="flex items-center gap-1">
                      <span class="i-lucide-calendar text-xs" />
                      {{ formatDate(appointment.start_time) }}
                    </span>
                    <span class="flex items-center gap-1">
                      <span class="i-lucide-clock text-xs" />
                      {{ formatTime(appointment.start_time) }} - {{ formatTime(appointment.end_time) }}
                    </span>
                  </div>
                  <div v-if="appointment.location" class="flex items-center gap-1 text-sm text-slate-400 mt-1">
                    <span class="i-lucide-map-pin text-xs" />
                    {{ appointment.location }}
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <span
                    :class="getStatusClass(appointment.status)"
                    class="px-2 py-1 text-xs font-medium rounded-full"
                  >
                    {{ getStatusLabel(appointment.status) }}
                  </span>
                  <button
                    class="p-1 text-slate-400 hover:text-red-500 rounded"
                    @click.stop="deleteAppointment(appointment)"
                  >
                    <span class="i-lucide-trash-2 text-sm" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Past Appointments -->
        <div v-if="pastAppointments.length > 0">
          <h3 class="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-2">
            <span class="i-lucide-history text-slate-400" />
            {{ t('APPOINTMENTS.PAST') }}
          </h3>
          <div class="space-y-2">
            <div
              v-for="appointment in pastAppointments"
              :key="appointment.id"
              class="bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-700/50 p-4 opacity-75 hover:opacity-100 transition-opacity cursor-pointer"
              @click="editAppointment(appointment)"
            >
              <div class="flex items-start justify-between">
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <div
                      class="w-3 h-3 rounded-full flex-shrink-0 opacity-50"
                      :style="{ backgroundColor: appointment.color || '#3b82f6' }"
                    />
                    <h4 class="font-medium text-slate-600 dark:text-slate-300 truncate">
                      {{ appointment.title }}
                    </h4>
                  </div>
                  <div class="flex items-center gap-4 text-sm text-slate-400">
                    <span class="flex items-center gap-1">
                      <span class="i-lucide-calendar text-xs" />
                      {{ formatDate(appointment.start_time) }}
                    </span>
                    <span class="flex items-center gap-1">
                      <span class="i-lucide-clock text-xs" />
                      {{ formatTime(appointment.start_time) }}
                    </span>
                  </div>
                </div>
                <span
                  :class="getStatusClass(appointment.status)"
                  class="px-2 py-1 text-xs font-medium rounded-full opacity-75"
                >
                  {{ getStatusLabel(appointment.status) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Appointment Modal -->
    <AppointmentModal
      v-if="showModal"
      :show="showModal"
      :appointment="selectedAppointment"
      :initial-date="selectedDate"
      :prefilled-contact-id="contactId"
      :prefilled-conversation-id="Number(conversationId)"
      @close="closeModal"
      @save="handleSaveAppointment"
      @delete="handleDeleteFromModal"
    />
  </div>
</template>

<style scoped>
.appointments-tab {
  min-height: 400px;
}
</style>
